import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import {
    Box,
    IconButton,
    TableBody,
    TableRow,
    Tooltip,
    Typography,
    styled,
    useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';

import ContentContainer from '@libComponents/ContentContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import DialogWrapper from '@libComponents/Dialog';
import { Button, Pagination } from '@libComponents';
import {
    createFetchPaginatedDataset,
    datasetLoadingSelector,
    entitySelector
} from '@libs/datasets';

import { DOMAIN_NAME, ROWS_PER_PAGE, STATUS } from '../common';
import { MY_CHANGE_REQUESTS } from '../redux.datasets';
import { cancelMyChangeRequest } from '../redux.actions';
import ChangeRequestDetailsDialog from './ChangeRequestDetailsDialog';
import StatusChip from './StatusChip';

const StyledBox = styled(Box)(({ theme }) => ({
    width: '100%',
    overflow: 'auto',
    '& table': { tableLayout: 'fixed' },
    '& .MuiTableRow-root': {
        transition: 'background-color 0.2s ease',
        '&:hover': { backgroundColor: theme.palette.action.hover }
    }
}));

const TABLE_COLUMNS = {
    createdAt: { label: 'Requested On', width: '20%' },
    changes: { label: 'Requested Changes', width: '38%' },
    status: { label: 'Status', width: '14%' },
    updatedBy: { label: 'Reviewed By', width: '16%' }
};

const defaultProps = { entity: MY_CHANGE_REQUESTS, domain: DOMAIN_NAME };

const dataSelector = createSelector(
    (state) => entitySelector(state, DOMAIN_NAME, MY_CHANGE_REQUESTS),
    (resp) => (resp && resp.data ? resp.data : [])
);

const totalRowsSelector = createCachedSelector(entitySelector, (resp) =>
    resp && resp.total ? parseInt(resp.total) : 0
)((state, domain, entity) => `@data-${domain}-${entity}`);

const mapState = (state, { domain, entity }) => ({
    data: dataSelector(state, domain, entity),
    loading: datasetLoadingSelector(state, domain, entity),
    totalRows: totalRowsSelector(state, domain, entity)
});

const mapDispatch = (dispatch, { entity }) => ({
    fetchDataset: ({ offset = 0, limit = ROWS_PER_PAGE } = {}) =>
        dispatch(
            createFetchPaginatedDataset({ entity, domain: DOMAIN_NAME })({
                limit,
                offset,
                shouldAppend: false,
                'sort-descending': true
            })
        )
});

/**
 * The employee's own change-request history — rendered as a tab on the
 * profile page. Pending requests can be opened and cancelled.
 */
const MyChangeRequests = connect(
    mapState,
    mapDispatch
)(({ data = [], totalRows = 0, fetchDataset, refreshToken = 0 }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(null);

    const refetch = useCallback(
        (page = currentPage) =>
            fetchDataset({
                offset: (page - 1) * ROWS_PER_PAGE,
                limit: ROWS_PER_PAGE
            }),
        [fetchDataset, currentPage]
    );

    useEffect(() => {
        setCurrentPage(1);
        fetchDataset({ offset: 0 });
    }, [fetchDataset, refreshToken]);

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
        refetch(newPage);
    };

    const handleCancel = () => {
        const request = confirmCancel;
        setConfirmCancel(null);
        setSelected(null);
        dispatch(
            cancelMyChangeRequest({
                payload: { id: request.id },
                handleSubmit: () => refetch()
            })
        );
    };

    const summarizeChanges = (changes = []) =>
        changes.map((change) => change.label).join(', ');

    return (
        <ContentContainer
            title='My Change Requests'
            headerBg={theme.palette.background.mainHeader}
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.entries(TABLE_COLUMNS).map(
                            ([key, { label, width }], index) => (
                                <TableCellContainer
                                    key={key}
                                    typography='caption'
                                    sx={{
                                        pl: index === 0 ? 4 : 2,
                                        width,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {label}
                                </TableCellContainer>
                            )
                        )}
                        <TableCellContainer
                            typography='caption'
                            align='right'
                            sx={{ pr: 4, width: '12%', fontWeight: 'bold' }}
                        >
                            Action
                        </TableCellContainer>
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setSelected(row)}
                            >
                                <TableCellContainer sx={{ pl: 4 }}>
                                    {dayjs(row.createdAt).format('MMM DD, YYYY (ddd)')}
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Tooltip
                                        title={summarizeChanges(row.changes)}
                                        arrow
                                        placement='top'
                                    >
                                        <Typography variant='body2' noWrap>
                                            {`${row.changes?.length || 0} field${
                                                (row.changes?.length || 0) > 1 ? 's' : ''
                                            } — ${summarizeChanges(row.changes)}`}
                                        </Typography>
                                    </Tooltip>
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <StatusChip status={row.status} />
                                </TableCellContainer>
                                <TableCellContainer sx={{ pl: 2 }}>
                                    <Typography variant='body2' noWrap>
                                        {row.updatedBy || '—'}
                                    </Typography>
                                </TableCellContainer>
                                <TableCellContainer
                                    align='right'
                                    sx={{ pr: 4 }}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            gap: 0.5,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <Tooltip title='View details' arrow placement='top'>
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={() => setSelected(row)}
                                            >
                                                <VisibilityIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        {row.status === STATUS.pending && (
                                            <Tooltip title='Withdraw' arrow placement='top'>
                                                <IconButton
                                                    size='small'
                                                    color='error'
                                                    onClick={() => setConfirmCancel(row)}
                                                >
                                                    <CancelIcon fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCellContainer>
                            </TableRow>
                        ))}
                        {!data.length && (
                            <TableRow>
                                <TableCellContainer
                                    colSpan={5}
                                    align='center'
                                    sx={{ py: 6, color: 'text.secondary' }}
                                >
                                    No change requests yet. Use “Request a Change” to
                                    ask for an update to your general information.
                                </TableCellContainer>
                            </TableRow>
                        )}
                    </TableBody>
                </TableContainer>
                {totalRows > ROWS_PER_PAGE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={Math.ceil(totalRows / ROWS_PER_PAGE)}
                            page={currentPage}
                            onChange={handleChangePage}
                            sx={{ display: 'flex', justifyContent: 'center' }}
                        />
                    </Box>
                )}
            </StyledBox>

            <ChangeRequestDetailsDialog
                open={!!selected}
                onClose={() => setSelected(null)}
                request={selected}
                mode='view'
                canCancel
                onCancel={(request) => setConfirmCancel(request)}
            />

            <DialogWrapper
                open={!!confirmCancel}
                onClose={() => setConfirmCancel(null)}
                sx={{ textAlign: 'center' }}
            >
                <Typography variant='h6' sx={{ mb: 1 }}>
                    Withdraw this change request?
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    The request will be withdrawn and the admin will no longer
                    see it as pending.
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 3
                    }}
                >
                    <Button
                        color='tertiary'
                        onClick={() => setConfirmCancel(null)}
                    >
                        Keep
                    </Button>
                    <Button color='error' onClick={handleCancel}>
                        Withdraw
                    </Button>
                </Box>
            </DialogWrapper>
        </ContentContainer>
    );
});

MyChangeRequests.defaultProps = defaultProps;

export default MyChangeRequests;
